import React from 'react';
import { EditorialNav, EditorialFooter } from './landing/EditorialShell';
import EditorialArticle from './landing/EditorialArticle';
import '../assets/scss/landing-v2.scss';

const PrivacyPage: React.FC = () => (
    <div
        className="lp2"
        style={{
            background: '#0a0a0a',
            color: '#f4f2ee',
            fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, sans-serif",
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
        }}
    >
        <EditorialNav />
        <main style={{ flex: 1 }}>
            <EditorialArticle eyebrow="LEGAL" title="Privacy Policy" subtitle="Effective date: March 25, 2026">
                <h2>1. Introduction</h2>
                <p>
                    This Privacy Policy explains how{' '}
                    <strong>
                        Nazar Gryshchuk, conducting sole-trader business activity (Jednoosobowa Działalność
                        Gospodarcza, JDG) registered in Poland
                    </strong>
                    , operating under the brand <strong>&quot;DoYourStep&quot;</strong>{' '}
                    (&quot;Operator,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) collects, uses,
                    discloses, and protects your personal information when you use our website at
                    doyourstep.com and related services (the &quot;Service&quot;).
                </p>
                <p>
                    We are committed to protecting your privacy and handling your data in accordance with applicable
                    data protection laws, including the General Data Protection Regulation (GDPR) for users in the
                    European Economic Area (EEA) and the California Consumer Privacy Act (CCPA) for California
                    residents.
                </p>
                <p>
                    By using the Service, you consent to the collection and use of your information as described in
                    this Privacy Policy.
                </p>

                <h2>2. Data Controller</h2>
                <p>
                    Nazar Gryshchuk (sole trader, JDG, Poland), operating as DoYourStep, is the data
                    controller for the personal information collected through the Service, except for payment
                    data, which is controlled by Polar Software Inc. (&quot;Polar&quot;), our payment
                    processor and Merchant of Record. For payment-related data inquiries, please refer to{' '}
                    <a href="https://polar.sh/legal/privacy" target="_blank" rel="noopener noreferrer">
                        Polar&apos;s Privacy Policy
                    </a>
                    .
                </p>

                <h2>3. Information We Collect</h2>
                <h3>3.1 Information You Provide</h3>
                <ul>
                    <li>
                        <strong>Account information:</strong> email address, name (optional), and password when you
                        create an account
                    </li>
                    <li>
                        <strong>Diagnostic responses:</strong> your answers to the behavioral assessment questions
                    </li>
                    <li>
                        <strong>Communication data:</strong> messages you send to us via email or the contact form
                    </li>
                </ul>

                <h3>3.2 Payment Information</h3>
                <p>
                    All payment information (credit card numbers, billing addresses, etc.) is collected and
                    processed exclusively by <strong>Polar</strong>, our Merchant of Record. We do not store,
                    process, or have access to your full payment card details. Polar processes payments in
                    compliance with PCI DSS Level 1 standards.
                </p>

                <h3>3.3 Automatically Collected Information</h3>
                <ul>
                    <li>
                        <strong>Device data:</strong> device type, operating system, browser type and version, screen
                        resolution
                    </li>
                    <li>
                        <strong>Usage data:</strong> pages visited, time spent on pages, click patterns, diagnostic
                        completion rates
                    </li>
                    <li>
                        <strong>Network data:</strong> IP address, approximate geographic location (country/city
                        level), referring URL
                    </li>
                </ul>

                <h3>3.4 Cookies and Tracking Technologies</h3>
                <p>We use the following categories of cookies and similar technologies:</p>
                <ul>
                    <li>
                        <strong>Essential cookies:</strong> required for the Service to function (authentication,
                        security, session management)
                    </li>
                    <li>
                        <strong>Analytics cookies:</strong> help us understand how users interact with the Service
                        (via Amplitude and Google Analytics)
                    </li>
                    <li>
                        <strong>Marketing cookies:</strong> used to deliver relevant advertisements and measure
                        campaign effectiveness (via Google Tag Manager)
                    </li>
                </ul>
                <p>
                    You can manage your cookie preferences through the cookie consent banner displayed when you
                    first visit the Service, or through your browser settings. Disabling certain cookies may affect
                    the functionality of the Service.
                </p>

                <h2>4. How We Use Your Information</h2>
                <p>We process your personal information for the following purposes and legal bases:</p>
                <ul>
                    <li>
                        <strong>Service delivery</strong> (contractual necessity): to generate your behavioral profile
                        and personalized protocol based on your diagnostic responses
                    </li>
                    <li>
                        <strong>Account management</strong> (contractual necessity): to create and manage your
                        account, authenticate your identity, and provide customer support
                    </li>
                    <li>
                        <strong>Communication</strong> (legitimate interest / consent): to send you service-related
                        notifications, respond to inquiries, and, with your consent, send marketing communications
                    </li>
                    <li>
                        <strong>Analytics and improvement</strong> (legitimate interest): to analyze usage patterns,
                        improve the Service, and develop new features using anonymized and aggregated data
                    </li>
                    <li>
                        <strong>Security and fraud prevention</strong> (legitimate interest): to detect, prevent, and
                        address technical issues, fraud, and abuse
                    </li>
                    <li>
                        <strong>Legal compliance</strong> (legal obligation): to comply with applicable laws,
                        regulations, and legal processes
                    </li>
                </ul>

                <h2>5. Data Sharing and Third Parties</h2>
                <p>We share your personal information only in the following circumstances:</p>
                <h3>5.1 Service Providers</h3>
                <ul>
                    <li>
                        <strong>Polar Software Inc.</strong> - payment processing, invoicing, tax compliance
                        (Merchant of Record)
                    </li>
                    <li>
                        <strong>Amplitude</strong> - product analytics and event tracking
                    </li>
                    <li>
                        <strong>Google Analytics / Google Tag Manager</strong> - website analytics and marketing
                    </li>
                    <li>
                        <strong>Cloud hosting providers</strong> - secure data storage and infrastructure
                    </li>
                </ul>
                <p>
                    All service providers are contractually obligated to process your data only as instructed by us
                    and to maintain appropriate security measures.
                </p>

                <h3>5.2 Legal Requirements</h3>
                <p>
                    We may disclose your information if required by law, regulation, legal process, or governmental
                    request, or when we believe disclosure is necessary to protect our rights, your safety, or the
                    safety of others.
                </p>

                <h3>5.3 Business Transfers</h3>
                <p>
                    In the event of a merger, acquisition, reorganization, or sale of assets, your information may
                    be transferred to the acquiring entity. We will notify you of any such change and any choices
                    you may have regarding your data.
                </p>

                <h3>5.4 No Sale of Personal Data</h3>
                <p>
                    We do not sell, rent, or trade your personal information to third parties for their marketing
                    purposes.
                </p>

                <h2>6. Data Retention</h2>
                <p>
                    We retain your personal information for as long as your account is active or as needed to
                    provide the Service. Specifically:
                </p>
                <ul>
                    <li>
                        <strong>Account data:</strong> retained until you request account deletion
                    </li>
                    <li>
                        <strong>Diagnostic results:</strong> retained as long as your account is active, to allow
                        retesting and progress tracking
                    </li>
                    <li>
                        <strong>Communication records:</strong> retained for up to 2 years after the last interaction
                    </li>
                    <li>
                        <strong>Analytics data:</strong> retained in anonymized form indefinitely for research and
                        improvement purposes
                    </li>
                </ul>
                <p>
                    After account deletion, we may retain certain data in anonymized form or as required by law
                    (e.g., financial records for tax compliance, typically 7 years).
                </p>

                <h2>7. Your Rights</h2>
                <p>Depending on your location, you have the following rights regarding your personal data:</p>
                <h3>For all users:</h3>
                <ul>
                    <li>
                        <strong>Access:</strong> request a copy of the personal data we hold about you
                    </li>
                    <li>
                        <strong>Correction:</strong> request correction of inaccurate or incomplete data
                    </li>
                    <li>
                        <strong>Deletion:</strong> request deletion of your personal data and account
                    </li>
                    <li>
                        <strong>Opt-out:</strong> unsubscribe from marketing communications at any time
                    </li>
                </ul>

                <h3>Additional rights for EEA residents (GDPR):</h3>
                <ul>
                    <li>
                        <strong>Portability:</strong> receive your data in a structured, machine-readable format
                    </li>
                    <li>
                        <strong>Restriction:</strong> request restriction of processing under certain circumstances
                    </li>
                    <li>
                        <strong>Objection:</strong> object to processing based on legitimate interests
                    </li>
                    <li>
                        <strong>Withdraw consent:</strong> withdraw consent at any time where processing is based on
                        consent
                    </li>
                    <li>
                        <strong>Complaint:</strong> lodge a complaint with your local data protection authority
                    </li>
                </ul>

                <h3>Additional rights for California residents (CCPA):</h3>
                <ul>
                    <li>
                        <strong>Know:</strong> know what personal information is collected and how it is used
                    </li>
                    <li>
                        <strong>Delete:</strong> request deletion of personal information
                    </li>
                    <li>
                        <strong>Non-discrimination:</strong> not be discriminated against for exercising your privacy
                        rights
                    </li>
                </ul>
                <p>
                    To exercise any of these rights, contact us at <strong>support@doyourstep.com</strong>. We will
                    respond within 30 days (or within the timeframe required by applicable law).
                </p>

                <h2>8. Data Security</h2>
                <p>
                    We implement appropriate technical and organizational security measures to protect your personal
                    information, including:
                </p>
                <ul>
                    <li>Encryption of data in transit (TLS/SSL) and at rest</li>
                    <li>Secure authentication mechanisms</li>
                    <li>Regular security assessments and updates</li>
                    <li>Access controls limiting data access to authorized personnel only</li>
                </ul>
                <p>
                    While we strive to protect your personal information, no method of transmission over the
                    Internet or electronic storage is 100% secure. We cannot guarantee absolute security but will
                    promptly notify you and relevant authorities of any data breach as required by law.
                </p>

                <h2>9. International Data Transfers</h2>
                <p>
                    Your information may be transferred to and processed in countries outside your country of
                    residence, including the United States, where data protection laws may differ. When we transfer
                    data from the EEA, we ensure appropriate safeguards are in place, such as Standard Contractual
                    Clauses approved by the European Commission.
                </p>

                <h2>10. Children&apos;s Privacy</h2>
                <p>
                    The Service is not intended for individuals under 18 years of age. We do not knowingly collect
                    personal information from anyone under 18. If we become aware that we have collected personal
                    information from a minor, we will take immediate steps to delete that information. If you are a
                    parent or guardian and believe your child has provided us with personal data, please contact us
                    at support@doyourstep.com.
                </p>

                <h2>11. Third-Party Links</h2>
                <p>
                    The Service may contain links to third-party websites or services that are not operated by us.
                    We have no control over and assume no responsibility for the content, privacy policies, or
                    practices of any third-party sites. We encourage you to review the privacy policy of every site
                    you visit.
                </p>

                <h2>12. Changes to This Policy</h2>
                <p>
                    We may update this Privacy Policy from time to time. If we make material changes, we will notify
                    you by updating the &quot;Effective date&quot; at the top of this page and, where practical, by
                    sending an email notification. Your continued use of the Service after changes are posted
                    constitutes your acceptance of the updated Privacy Policy.
                </p>

                <h2>13. Contact Us</h2>
                <p>
                    If you have any questions, concerns, or requests regarding this Privacy Policy or our data
                    practices, please contact us:
                </p>
                <p>
                    <strong>Email:</strong> support@doyourstep.com
                </p>
                <p>
                    For payment-related inquiries, you may also contact Polar directly through their{' '}
                    <a href="https://polar.sh/legal/privacy" target="_blank" rel="noopener noreferrer">
                        support page
                    </a>
                    .
                </p>
            </EditorialArticle>
        </main>
        <EditorialFooter />
    </div>
);

export default PrivacyPage;
